import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { cn } from "@/utils/cn";

interface LaunchOfferSectionProps {
  className?: string;
}

const OFFRE_AVANTAGES = [
  { icon: "ri-home-4-line", label: "Gestion des biens" },
  { icon: "ri-building-2-line", label: "Gestion des immeubles" },
  { icon: "ri-user-star-line", label: "Gestion des propriétaires" },
  { icon: "ri-user-3-line", label: "Gestion des locataires" },
  { icon: "ri-file-text-line", label: "Gestion des contrats" },
  { icon: "ri-wallet-3-line", label: "Suivi des loyers" },
  { icon: "ri-hand-coin-line", label: "Enregistrement des paiements" },
  { icon: "ri-alert-line", label: "Suivi des impayés" },
];

/**
 * Offre de lancement publique : 6 mois gratuits, accès complet, sans limite de
 * formule. Réservée aux pages publiques (Accueil, Tarifs, Abonnement) — les
 * formules payantes restent affichées et sélectionnables dans les espaces
 * internes d'abonnement (réabonnement après l'essai).
 */
export default function LaunchOfferSection({ className }: LaunchOfferSectionProps) {
  return (
    <Reveal variant="scale" className={cn("w-full", className)}>
      <div className="relative overflow-hidden rounded-2xl border border-primary-200 bg-background-50 p-6 md:p-10">
        <div className="float-slow pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary-100/70" />
        <div className="float-slower pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-accent-100/60" />

        <div className="relative grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-12">
          <div className="flex flex-col items-start">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent-300 bg-accent-100 px-3 py-1 text-xs font-label font-semibold uppercase tracking-wide text-accent-900">
              <i className="ri-gift-line text-[13px]" aria-hidden="true" />
              Offre de lancement
            </span>

            <h2 className="mt-5 text-2xl md:text-4xl font-heading font-semibold leading-tight text-foreground-950">
              6 mois gratuits, accès complet à toutes les fonctionnalités.
            </h2>

            <div className="mt-5 flex flex-wrap items-end gap-x-3 gap-y-1">
              <span className="text-4xl md:text-5xl font-heading font-semibold text-primary-700">0 FCFA</span>
              <span className="pb-1 text-sm md:text-base text-foreground-600">pendant 6 mois</span>
            </div>

            <p className="mt-4 max-w-md text-sm md:text-base leading-relaxed text-foreground-600">
              Pendant l'offre de lancement, les nouveaux propriétaires et les agences immobilières
              profitent de la plateforme sans frais et sans limites de formule.
            </p>

            <Link
              to="/inscription"
              className="group mt-7 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-base font-label font-medium text-background-50 hover:bg-primary-600 hover:-translate-y-0.5 transition-all cursor-pointer sm:w-auto"
            >
              Créer mon compte gratuitement
              <i
                className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>

            <p className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-shield-check-line text-secondary-600" aria-hidden="true" />
              Sans engagement. Aucun paiement demandé pendant les 6 mois offerts.
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/70 p-5 md:p-6">
            <h3 className="text-sm font-label font-semibold uppercase tracking-wide text-foreground-700">
              Tout est inclus, sans aucune limite
            </h3>
            <ul className="mt-4 grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2">
              {OFFRE_AVANTAGES.map((avantage) => (
                <li key={avantage.label} className="flex items-start gap-2 text-sm text-foreground-800">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                    <i className={`${avantage.icon} text-[13px]`} aria-hidden="true" />
                  </span>
                  {avantage.label}
                </li>
              ))}
            </ul>
            <div className="mt-5 flex items-center gap-2 border-t border-background-200 pt-4 text-sm font-label font-medium text-secondary-800">
              <i className="ri-infinity-line" aria-hidden="true" />
              Aucune limite de biens, immeubles, locataires, contrats ou employés.
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}